import { Injectable } from '@angular/core'
import { Action, select, Store } from '@ngrx/store'
import { Actions, Effect, ofType } from '@ngrx/effects'
import { TranslateService } from '@ngx-translate/core'

import { Observable, of } from 'rxjs'
import { catchError, map, switchMap, tap, withLatestFrom, filter } from 'rxjs/operators';
import { toPayload, withPredicateFrom } from '../../core/helpers/rx-pipes'

import { UserService } from '../services/user.service'

import * as user from '../actions/user'
import * as pages from '../../base/actions/pages.actions'
import * as menus from '../../base/actions/menus.actions'
import * as status from '../../base/actions/status.actions'

import * as fromUser from '../reducers'
import * as fromAuth from '../../auth/reducers'

import * as moment from 'moment'

import { Preferences, UserContext } from '../models/auth'
import { CustomersService } from '../services/customers.service';
import { Router } from '@angular/router';

@Injectable()
export class UserEffects {
    constructor(
        private actions$: Actions,
        private userService: UserService,
        private router: Router,
        private customerService: CustomersService,
        private store: Store<fromUser.State>,
        private translate: TranslateService
    ) { }

    @Effect()
    getUserContext$: Observable<Action> =
        this.actions$.ofType<user.Get>(user.GET)
            .pipe(
                switchMap(() => {
                    return this.userService.userContext()
                        .pipe(
                            map(context => new user.GetSuccess(context)),
                            catchError(e => of(new user.GetFailed(e)))
                        )
                })
            )
    @Effect()
    setCustomer$: Observable<Action> =
        this.actions$.ofType<user.SetCustomer>(user.SET_CUSTOMER)
          .pipe(
            toPayload<string>(),
            switchMap(customerId => {
              return this.customerService.getCustomerById(customerId)
                .pipe(
                  map(customer => new user.SetCustomerSuccess(customer)),
                  catchError(e => of(new user.SetCustomerFailed(e)))
                )
            })
          )

    @Effect()
    setCustomerSuccess$: Observable<Action> =
      this.actions$.ofType<user.SetCustomerSuccess>(user.SET_CUSTOMER_SUCCESS)
        .pipe(
          switchMap(action => {
            this.router.navigate(['/home'])
            return [new pages.Init(true), new menus.Init(true), new status.SetHeader(true)]
          })
        )


    @Effect({ dispatch: false })
    applyLanguageAfterLogin$: Observable<any> =
        this.actions$.ofType<user.GetSuccess>(user.GET_SUCCESS)
            .pipe(
                toPayload<UserContext>(),
                map(user => user.preferences.language),
                tap(this.changeLanguage.bind(this))
            )

    @Effect()
    addHeaderAfterLogin$: Observable<Action> = this.actions$.pipe(
      ofType(user.GET_SUCCESS),
      withLatestFrom(
        this.store.pipe(select(fromAuth.isSalesRep))
      ),
      filter(([action, isSalesRep]) => !isSalesRep),
      map(() => new status.SetHeader(true))
    )

    @Effect()
    setLanguage$: Observable<Action> =
        this.actions$.ofType<user.SetLanguage>(user.SET_LANGUAGE)
            .pipe(
                toPayload<string>(),
                withPredicateFrom(this.store.pipe(select(fromUser.getLoggedIn))),
                withLatestFrom(
                    this.store.pipe(select(fromUser.getUserContext)),
                    (lang, userCtx) => ({ ...userCtx, language: lang })
                ),
                map(prefs => new user.Set(prefs))
            )

    @Effect()
    savePreferences$: Observable<Action> =
        this.actions$.ofType<user.Set>(user.SET)
            .pipe(
                toPayload<Preferences>(),
                switchMap(preferences => {
                    return this.userService.savePreferences(preferences)
                        .pipe(
                            map(pref => new user.SetSuccess(pref)),
                            catchError(e => of(new user.SetFailed(e)))
                        )
                })
            )

    @Effect({ dispatch: false })
    applyLanguageAfterSet: Observable<any> =
        this.actions$.ofType<user.SetSuccess>(user.SET_SUCCESS)
            .pipe(
                toPayload<Preferences>(),
                map(prefs => prefs.language)
            ).pipe(
            tap(this.changeLanguage.bind(this)))

    @Effect({dispatch: false})
    changeLangPreLogin$ =
        this.actions$.ofType<user.SetLanguage>(user.SET_LANGUAGE)
            .pipe(
              toPayload<string>(),
              withPredicateFrom(
                    this.store.pipe(select(fromUser.getLoggedIn)),
                    loggedIn => !loggedIn
                )
            ).pipe(
            tap(state => this.changeLanguage(state)))

    changeLanguage(lang: string) {
      this.translate.use(lang)
        moment.locale(lang)
    }
}
